import { createActions, createState, createReducer } from 'reduxsauce-crud'
import { clone, findIndex, propEq, reject } from 'ramda'

const d = { deep: true }

// Create actions and types
export const DocumentsRedux = createActions(
  {
    // Reset actions
    resetError: [],
    resetWizard: [],

    // Student repository actions
    fetchStudentDocumentsRequest: ['studentId', 'filters'],
    fetchStudentDocumentsSuccess: ['documents', 'meta'],
    fetchStudentDocumentsError: ['error'],

    // Search with debounce
    searchStudentDocuments: ['studentId', 'searchTerm'],

    // Delete student document
    deleteStudentDocumentRequest: ['studentId', 'documentId'],
    deleteStudentDocumentSuccess: ['documentId'],
    deleteStudentDocumentError: ['error'],

    // Document library actions
    fetchDocumentsRequest: ['filters'],
    fetchDocumentsSuccess: ['documents', 'meta'],
    fetchDocumentsError: ['error'],

    // Document versions
    fetchDocumentVersionsRequest: ['documentId'],
    fetchDocumentVersionsSuccess: ['documentId', 'versions'],
    fetchDocumentVersionsError: ['error'],

    // Distribution history
    fetchDistributionHistoryRequest: ['documentId'],
    fetchDistributionHistorySuccess: ['documentId', 'history'],
    fetchDistributionHistoryError: ['error'],

    // Download version
    downloadVersionRequest: ['documentId', 'versionId'],
    downloadVersionSuccess: [],
    downloadVersionError: ['error'],

    // Download student document
    downloadStudentDocumentRequest: ['studentId', 'documentId'],
    downloadStudentDocumentSuccess: [],
    downloadStudentDocumentError: ['error'],

    // Wizard actions
    openWizard: ['preselectedDocumentId', 'preselectedStudentId'],
    closeWizard: [],
    nextStep: [],
    prevStep: [],

    // Step 1: Select documents
    selectDocument: ['document'],
    deselectDocument: ['documentId'],
    selectAllDocuments: ['documents'],
    clearDocuments: [],

    // Step 2: Select students
    selectStudent: ['student'],
    deselectStudent: ['studentId'],
    selectStudents: ['students'],
    clearStudents: [],

    // Step 3: Distribute
    setEmailNotification: ['sendEmail'],
    checkDuplicatesRequest: ['documentIds', 'studentIds'],
    checkDuplicatesSuccess: ['duplicates'],
    checkDuplicatesError: ['error'],
    distributeDocumentsRequest: ['payload'],
    distributeDocumentsSuccess: ['result'],
    distributeDocumentsError: ['error'],

    // UI state
    setViewMode: ['mode'], // 'table' | 'grid'
    setFilters: ['filters'],
  },
  {
    prefix: 'DOCUMENTS_',
    defaultActions: {},
  },
)

const { Types, Creators } = DocumentsRedux
export const DocumentsTypes = Types
export default Creators

// Initial state
export const INITIAL_STATE = createState(
  {
    // Student repository state
    studentRepository: {
      fetching: false,
      error: null,
      documents: [],
      meta: {
        current_page: 1,
        total: 0,
        per_page: 20,
        last_page: 1,
      },
      filters: {
        search: '',
        dateFrom: null,
        dateTo: null,
        sortBy: 'sent_at',
        sortOrder: 'desc',
      },
    },

    // Document library state
    library: {
      fetching: false,
      error: null,
      documents: [],
      meta: {
        current_page: 1,
        total: 0,
        per_page: 20,
        last_page: 1,
      },
      filters: {
        search: '',
        type: null,
        authorId: null,
        dateFrom: null,
        dateTo: null,
        sortBy: 'created_at',
        sortOrder: 'desc',
      },
      viewMode: 'grid', // 'table' | 'grid'
    },

    // Document versions state
    versions: {
      fetching: false,
      error: null,
      data: {}, // { [documentId]: [versions] }
    },

    // Distribution history state
    history: {
      fetching: false,
      error: null,
      data: {}, // { [documentId]: [history entries] }
    },

    // Delete state
    delete: {
      fetching: false,
      error: null,
      deletingId: null,
    },

    // Wizard state
    wizard: {
      isOpen: false,
      currentStep: 1,
      selectedDocuments: [],
      selectedStudents: [],
      sendEmail: true,
      duplicates: [],
      checkingDuplicates: false,
      distributing: false,
      error: null,
      result: null,
    },
  },
  {},
)

// Reducers
const resetError = state =>
  state.merge(
    {
      studentRepository: { ...state.studentRepository, error: null },
      library: { ...state.library, error: null },
      delete: { ...state.delete, error: null },
      wizard: { ...state.wizard, error: null },
    },
    d,
  )

const resetWizard = state =>
  state.merge(
    {
      wizard: {
        ...INITIAL_STATE.wizard,
      },
    },
    d,
  )

// Student repository reducers
const fetchStudentDocumentsRequest = state =>
  state.merge(
    {
      studentRepository: {
        ...state.studentRepository,
        fetching: true,
        error: null,
      },
    },
    d,
  )

const fetchStudentDocumentsSuccess = (state, { documents, meta }) =>
  state.merge(
    {
      studentRepository: {
        ...state.studentRepository,
        fetching: false,
        error: null,
        documents,
        meta,
      },
    },
    d,
  )

const fetchStudentDocumentsError = (state, { error }) =>
  state.merge(
    {
      studentRepository: {
        ...state.studentRepository,
        fetching: false,
        error,
      },
    },
    d,
  )

// Delete reducers
const deleteStudentDocumentRequest = (state, { documentId }) =>
  state.merge(
    {
      delete: {
        fetching: true,
        error: null,
        deletingId: documentId,
      },
    },
    d,
  )

const deleteStudentDocumentSuccess = (state, { documentId }) => {
  const newDocuments = reject(propEq('id', documentId), state.studentRepository.documents)
  return state.merge(
    {
      delete: {
        fetching: false,
        error: null,
        deletingId: null,
      },
      studentRepository: {
        ...state.studentRepository,
        documents: newDocuments,
        meta: {
          ...state.studentRepository.meta,
          total: state.studentRepository.meta.total - 1,
        },
      },
    },
    d,
  )
}

const deleteStudentDocumentError = (state, { error }) =>
  state.merge(
    {
      delete: {
        fetching: false,
        error,
        deletingId: null,
      },
    },
    d,
  )

// Library reducers
const fetchDocumentsRequest = state =>
  state.merge(
    {
      library: {
        ...state.library,
        fetching: true,
        error: null,
      },
    },
    d,
  )

const fetchDocumentsSuccess = (state, { documents, meta }) =>
  state.merge(
    {
      library: {
        ...state.library,
        fetching: false,
        error: null,
        documents,
        meta,
      },
    },
    d,
  )

const fetchDocumentsError = (state, { error }) =>
  state.merge(
    {
      library: {
        ...state.library,
        fetching: false,
        error,
      },
    },
    d,
  )

// Document versions reducers
const fetchDocumentVersionsRequest = state =>
  state.merge(
    {
      versions: {
        ...state.versions,
        fetching: true,
        error: null,
      },
    },
    d,
  )

const fetchDocumentVersionsSuccess = (state, { documentId, versions }) =>
  state.merge(
    {
      versions: {
        ...state.versions,
        fetching: false,
        error: null,
        data: {
          ...state.versions.data,
          [documentId]: versions,
        },
      },
    },
    d,
  )

const fetchDocumentVersionsError = (state, { error }) =>
  state.merge(
    {
      versions: {
        ...state.versions,
        fetching: false,
        error,
      },
    },
    d,
  )

// Distribution history reducers
const fetchDistributionHistoryRequest = state =>
  state.merge(
    {
      history: {
        ...state.history,
        fetching: true,
        error: null,
      },
    },
    d,
  )

const fetchDistributionHistorySuccess = (state, { documentId, history }) =>
  state.merge(
    {
      history: {
        ...state.history,
        fetching: false,
        error: null,
        data: {
          ...state.history.data,
          [documentId]: history,
        },
      },
    },
    d,
  )

const fetchDistributionHistoryError = (state, { error }) =>
  state.merge(
    {
      history: {
        ...state.history,
        fetching: false,
        error,
      },
    },
    d,
  )

// Wizard reducers
const openWizard = (state, { preselectedDocumentId, preselectedStudentId }) => {
  const wizard = {
    ...INITIAL_STATE.wizard,
    isOpen: true,
  }

  // Preselect document if provided
  if (preselectedDocumentId) {
    const document = state.library.documents.find(d => d.id === preselectedDocumentId)
    if (document) {
      wizard.selectedDocuments = [document]
    }
  }

  // Preselect student if provided
  if (preselectedStudentId) {
    // Student will be loaded by epic
    wizard.preselectedStudentId = preselectedStudentId
  }

  return state.merge({ wizard }, d)
}

const closeWizard = state =>
  state.merge(
    {
      wizard: {
        ...INITIAL_STATE.wizard,
      },
    },
    d,
  )

const nextStep = state =>
  state.merge(
    {
      wizard: {
        ...state.wizard,
        currentStep: state.wizard.currentStep + 1,
      },
    },
    d,
  )

const prevStep = state =>
  state.merge(
    {
      wizard: {
        ...state.wizard,
        currentStep: state.wizard.currentStep - 1,
      },
    },
    d,
  )

// Step 1 reducers
const selectDocument = (state, { document }) => {
  const alreadySelected = state.wizard.selectedDocuments.find(d => d.id === document.id)
  if (alreadySelected) {
    return state
  }

  return state.merge(
    {
      wizard: {
        ...state.wizard,
        selectedDocuments: [...state.wizard.selectedDocuments, document],
      },
    },
    d,
  )
}

const deselectDocument = (state, { documentId }) =>
  state.merge(
    {
      wizard: {
        ...state.wizard,
        selectedDocuments: reject(propEq('id', documentId), state.wizard.selectedDocuments),
      },
    },
    d,
  )

const selectAllDocuments = (state, { documents }) =>
  state.merge(
    {
      wizard: {
        ...state.wizard,
        selectedDocuments: documents,
      },
    },
    d,
  )

const clearDocuments = state =>
  state.merge(
    {
      wizard: {
        ...state.wizard,
        selectedDocuments: [],
      },
    },
    d,
  )

// Step 2 reducers
const selectStudent = (state, { student }) => {
  const alreadySelected = state.wizard.selectedStudents.find(s => s.id === student.id)
  if (alreadySelected) {
    return state
  }

  return state.merge(
    {
      wizard: {
        ...state.wizard,
        selectedStudents: [...state.wizard.selectedStudents, student],
      },
    },
    d,
  )
}

const deselectStudent = (state, { studentId }) =>
  state.merge(
    {
      wizard: {
        ...state.wizard,
        selectedStudents: reject(propEq('id', studentId), state.wizard.selectedStudents),
      },
    },
    d,
  )

const selectStudents = (state, { students }) => {
  // Avoid duplicates
  const currentIds = state.wizard.selectedStudents.map(s => s.id)
  const newStudents = students.filter(s => !currentIds.includes(s.id))

  return state.merge(
    {
      wizard: {
        ...state.wizard,
        selectedStudents: [...state.wizard.selectedStudents, ...newStudents],
      },
    },
    d,
  )
}

const clearStudents = state =>
  state.merge(
    {
      wizard: {
        ...state.wizard,
        selectedStudents: [],
      },
    },
    d,
  )

// Step 3 reducers
const setEmailNotification = (state, { sendEmail }) =>
  state.merge(
    {
      wizard: {
        ...state.wizard,
        sendEmail,
      },
    },
    d,
  )

const checkDuplicatesRequest = state =>
  state.merge(
    {
      wizard: {
        ...state.wizard,
        checkingDuplicates: true,
      },
    },
    d,
  )

const checkDuplicatesSuccess = (state, { duplicates }) =>
  state.merge(
    {
      wizard: {
        ...state.wizard,
        checkingDuplicates: false,
        duplicates,
      },
    },
    d,
  )

const checkDuplicatesError = (state, { error }) =>
  state.merge(
    {
      wizard: {
        ...state.wizard,
        checkingDuplicates: false,
        error,
      },
    },
    d,
  )

const distributeDocumentsRequest = state =>
  state.merge(
    {
      wizard: {
        ...state.wizard,
        distributing: true,
        error: null,
      },
    },
    d,
  )

const distributeDocumentsSuccess = (state, { result }) =>
  state.merge(
    {
      wizard: {
        ...state.wizard,
        distributing: false,
        error: null,
        result,
      },
    },
    d,
  )

const distributeDocumentsError = (state, { error }) =>
  state.merge(
    {
      wizard: {
        ...state.wizard,
        distributing: false,
        error,
      },
    },
    d,
  )

// UI reducers
const setViewMode = (state, { mode }) =>
  state.merge(
    {
      library: {
        ...state.library,
        viewMode: mode,
      },
    },
    d,
  )

const setFilters = (state, { filters }) =>
  state.merge(
    {
      library: {
        ...state.library,
        filters: {
          ...state.library.filters,
          ...filters,
        },
      },
    },
    d,
  )

// Create reducer
export const reducer = createReducer(INITIAL_STATE, {
  [Types.resetError]: resetError,
  [Types.resetWizard]: resetWizard,

  // Student repository
  [Types.fetchStudentDocumentsRequest]: fetchStudentDocumentsRequest,
  [Types.fetchStudentDocumentsSuccess]: fetchStudentDocumentsSuccess,
  [Types.fetchStudentDocumentsError]: fetchStudentDocumentsError,

  // Delete
  [Types.deleteStudentDocumentRequest]: deleteStudentDocumentRequest,
  [Types.deleteStudentDocumentSuccess]: deleteStudentDocumentSuccess,
  [Types.deleteStudentDocumentError]: deleteStudentDocumentError,

  // Library
  [Types.fetchDocumentsRequest]: fetchDocumentsRequest,
  [Types.fetchDocumentsSuccess]: fetchDocumentsSuccess,
  [Types.fetchDocumentsError]: fetchDocumentsError,

  // Versions
  [Types.fetchDocumentVersionsRequest]: fetchDocumentVersionsRequest,
  [Types.fetchDocumentVersionsSuccess]: fetchDocumentVersionsSuccess,
  [Types.fetchDocumentVersionsError]: fetchDocumentVersionsError,

  // History
  [Types.fetchDistributionHistoryRequest]: fetchDistributionHistoryRequest,
  [Types.fetchDistributionHistorySuccess]: fetchDistributionHistorySuccess,
  [Types.fetchDistributionHistoryError]: fetchDistributionHistoryError,

  // Wizard
  [Types.openWizard]: openWizard,
  [Types.closeWizard]: closeWizard,
  [Types.nextStep]: nextStep,
  [Types.prevStep]: prevStep,

  // Step 1
  [Types.selectDocument]: selectDocument,
  [Types.deselectDocument]: deselectDocument,
  [Types.selectAllDocuments]: selectAllDocuments,
  [Types.clearDocuments]: clearDocuments,

  // Step 2
  [Types.selectStudent]: selectStudent,
  [Types.deselectStudent]: deselectStudent,
  [Types.selectStudents]: selectStudents,
  [Types.clearStudents]: clearStudents,

  // Step 3
  [Types.setEmailNotification]: setEmailNotification,
  [Types.checkDuplicatesRequest]: checkDuplicatesRequest,
  [Types.checkDuplicatesSuccess]: checkDuplicatesSuccess,
  [Types.checkDuplicatesError]: checkDuplicatesError,
  [Types.distributeDocumentsRequest]: distributeDocumentsRequest,
  [Types.distributeDocumentsSuccess]: distributeDocumentsSuccess,
  [Types.distributeDocumentsError]: distributeDocumentsError,

  // UI
  [Types.setViewMode]: setViewMode,
  [Types.setFilters]: setFilters,
})



// WEBPACK FOOTER //
// ./src/Data/Redux/DocumentsRedux.js